import { useState } from "react";
import { api } from "../lib/api";
import { toast } from "sonner";
import { Input } from "./ui/input";
import { Checkbox } from "./ui/checkbox";
import { Loader2, CheckCircle2 } from "lucide-react";
import { useI18n } from "../i18n";

const empty = { first_name: "", last_name: "", email: "", city: "", postal_code: "", volunteer: false };

export const SupportSection = () => {
  const { t } = useI18n();
  const [form, setForm] = useState(empty);
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post("/supporters", form);
      setDone(true);
      toast.success(t("sup.toast_ok"));
      window.dispatchEvent(new Event("supporter-added"));
    } catch (err) {
      toast.error(err?.response?.data?.detail || t("sup.toast_err"));
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="soutenir" className="relative py-24 sm:py-32 bg-[#0B1121] overflow-hidden">
      <div className="absolute inset-0 grain opacity-[0.12] pointer-events-none" />
      <div className="absolute -top-20 -right-20 w-96 h-96 bg-[#001489] rounded-full blur-[120px] opacity-40" />
      <div className="absolute -bottom-20 -left-20 w-96 h-96 bg-[#E1000F] rounded-full blur-[130px] opacity-20" />

      <div className="max-w-7xl mx-auto px-5 sm:px-8 relative grid lg:grid-cols-2 gap-14 items-center">
        <div>
          <p className="font-mono-accent text-xs uppercase tracking-[0.3em] text-[#38BDF8] mb-4">{t("sup.eyebrow")}</p>
          <h2 className="font-display font-black tracking-tighter text-4xl sm:text-5xl lg:text-6xl text-white leading-[0.95]">
            {t("sup.title")}
          </h2>
          <p className="mt-6 text-lg text-white/70 max-w-md leading-relaxed">{t("sup.desc")}</p>
          <ul className="mt-8 space-y-3 text-white/80">
            {[t("sup.b1"), t("sup.b2"), t("sup.b3")].map((x) => (
              <li key={x} className="flex items-center gap-3">
                <CheckCircle2 size={18} className="text-[#38BDF8]" /> {x}
              </li>
            ))}
          </ul>
        </div>

        <div className="bg-white/70 backdrop-blur-xl border border-white/20 rounded-3xl p-8 sm:p-10 shadow-2xl">
          {done ? (
            <div data-testid="support-success" className="text-center py-10">
              <CheckCircle2 size={56} className="mx-auto text-[#001489]" />
              <h3 className="mt-5 font-display font-extrabold text-2xl text-[#0B1121]">{t("sup.success_title")}</h3>
              <p className="mt-2 text-slate-600">{t("sup.success_desc")}</p>
              <button
                data-testid="support-add-another"
                onClick={() => { setForm(empty); setDone(false); }}
                className="mt-6 text-[#001489] font-semibold underline underline-offset-4"
              >
                {t("sup.success_another")}
              </button>
            </div>
          ) : (
            <form onSubmit={submit} className="space-y-4" data-testid="supporter-form">
              <div className="grid grid-cols-2 gap-4">
                <Input required data-testid="supporter-first-name" className="h-12 bg-white" placeholder={t("sup.first")}
                  value={form.first_name} onChange={(e) => set("first_name", e.target.value)} />
                <Input required data-testid="supporter-last-name" className="h-12 bg-white" placeholder={t("sup.last")}
                  value={form.last_name} onChange={(e) => set("last_name", e.target.value)} />
              </div>
              <Input required type="email" data-testid="supporter-email" className="h-12 bg-white" placeholder={t("sup.email")}
                value={form.email} onChange={(e) => set("email", e.target.value)} />
              <div className="grid grid-cols-3 gap-4">
                <Input required data-testid="supporter-city" className="h-12 bg-white col-span-2" placeholder={t("sup.city")}
                  value={form.city} onChange={(e) => set("city", e.target.value)} />
                <Input required data-testid="supporter-postal" className="h-12 bg-white" placeholder={t("sup.postal")}
                  value={form.postal_code} onChange={(e) => set("postal_code", e.target.value)} />
              </div>
              <label className="flex items-center gap-3 py-2 cursor-pointer">
                <Checkbox data-testid="supporter-volunteer" checked={form.volunteer} onCheckedChange={(v) => set("volunteer", !!v)} />
                <span className="text-sm text-slate-700">{t("sup.volunteer")}</span>
              </label>
              <button
                type="submit"
                disabled={loading}
                data-testid="supporter-form-submit"
                className="w-full h-14 bg-[#0B1121] text-white font-semibold rounded-xl hover:bg-[#001489] transition-all disabled:opacity-60 flex items-center justify-center gap-2"
              >
                {loading ? <Loader2 className="animate-spin" size={20} /> : t("sup.submit")}
              </button>
              <p className="text-xs text-slate-500 text-center">{t("sup.privacy")}</p>
            </form>
          )}
        </div>
      </div>
    </section>
  );
};
